<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import EventForm from '@/components/organizer/events/EventForm.vue'
import EventLogoUpload from '@/components/organizer/events/EventLogoUpload.vue'
import EventEmailSettingsPanel from '@/components/organizer/events/EventEmailSettingsPanel.vue'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import type { StoreEventPayload } from '@/types/events'

interface EventFormData {
  name: string
  tagline: string
  description: string
  facebook_page: string
  instagram_page: string
  external_website_url: string
  mail_bcc: string
  contact_email: string
  maintenance: boolean
}

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

const { t } = useI18n()
const router = useRouter()
const { createEvent } = useEvents()

const step = ref<1 | 2 | 3>(1)
const createdEventId = ref<number | null>(null)
const createdEventSlug = ref<string | null>(null)
const currentLogoUrl = ref<string | null>(null)

const loading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')

const form = ref<EventFormData>({
  name: '',
  tagline: '',
  description: '',
  facebook_page: '',
  instagram_page: '',
  external_website_url: '',
  mail_bcc: '',
  contact_email: '',
  maintenance: false,
})

async function handleStep1Submit(data: StoreEventPayload): Promise<void> {
  errors.value = {}
  generalError.value = ''
  loading.value = true
  try {
    const event = await createEvent(data)
    createdEventId.value = event.id
    createdEventSlug.value = event.slug
    step.value = 2
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      if (e.response.status === 422 && e.response.data.errors) {
        errors.value = e.response.data.errors
      } else {
        generalError.value = t('events.form.error_generic')
      }
    } else {
      generalError.value = t('events.form.error_generic')
    }
  } finally {
    loading.value = false
  }
}

function finish(): void {
  void router.push(`/organizer/events/${createdEventSlug.value}`)
}
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div class="mb-6">
        <RouterLink
          to="/organizer/events"
          class="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          {{ t('events.detail.back_to_list') }}
        </RouterLink>
      </div>

      <!-- Step indicator -->
      <div class="mb-8">
        <ol class="flex items-center gap-0">
          <li
            v-for="(s, index) in [
              t('events.wizard.step_details'),
              t('events.wizard.step_logo'),
              t('events.wizard.step_email'),
            ]"
            :key="index"
            class="flex items-center"
            :class="index < 2 ? 'flex-1' : ''"
          >
            <div class="flex items-center gap-2 shrink-0">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors"
                :class="
                  step > index + 1
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : step === index + 1
                      ? 'border-emerald-500 text-emerald-400 bg-transparent'
                      : 'border-slate-700 text-slate-600 bg-transparent'
                "
              >
                <svg v-if="step > index + 1" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                </svg>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span
                class="text-sm font-medium hidden sm:block"
                :class="step === index + 1 ? 'text-white' : step > index + 1 ? 'text-emerald-400' : 'text-slate-600'"
              >
                {{ s }}
              </span>
            </div>
            <div
              v-if="index < 2"
              class="flex-1 h-px mx-3"
              :class="step > index + 1 ? 'bg-emerald-500' : 'bg-slate-700'"
            />
          </li>
        </ol>
      </div>

      <!-- Step 1: Event details -->
      <template v-if="step === 1">
        <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
          <h1 class="text-2xl font-bold text-white mb-6">{{ t('events.form.create_heading') }}</h1>

          <div
            v-if="generalError"
            class="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
          >
            {{ generalError }}
          </div>

          <EventForm
            v-model="form"
            :loading="loading"
            :errors="errors"
            :is-edit="false"
            :submit-label="t('events.wizard.next')"
            @submit="handleStep1Submit"
            @cancel="router.push('/organizer/events')"
          />
        </div>
      </template>

      <!-- Step 2: Logo -->
      <template v-else-if="step === 2 && createdEventId !== null && createdEventSlug !== null">
        <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
          <h2 class="text-xl font-bold text-white mb-6">{{ t('events.settings.tab_logo') }}</h2>
          <EventLogoUpload
            :event-slug="createdEventSlug"
            :current-logo-url="currentLogoUrl"
            @logo-updated="(url) => { currentLogoUrl = url }"
          />
        </div>

        <div class="mt-6 flex items-center justify-between">
          <p class="text-sm text-slate-500">{{ t('events.wizard.step_hint_optional') }}</p>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
              @click="step = 3"
            >
              {{ t('events.wizard.skip') }}
            </button>
            <button
              type="button"
              class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
              @click="step = 3"
            >
              {{ t('events.wizard.continue') }}
            </button>
          </div>
        </div>
      </template>

      <!-- Step 3: Email settings -->
      <template v-else-if="step === 3 && createdEventId !== null && createdEventSlug !== null">
        <EventEmailSettingsPanel :event-slug="createdEventSlug" />

        <div class="mt-6 flex items-center justify-between">
          <p class="text-sm text-slate-500">{{ t('events.wizard.step_hint_optional') }}</p>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
              @click="step = 2"
            >
              {{ t('events.wizard.back') }}
            </button>
            <button
              type="button"
              class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
              @click="finish"
            >
              {{ t('events.wizard.skip') }}
            </button>
            <button
              type="button"
              class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
              @click="finish"
            >
              {{ t('events.wizard.finish') }}
            </button>
          </div>
        </div>
      </template>
    </main>
  </OrganizerLayout>
</template>
